import React, { useMemo } from "react";

import { StatusPill } from "components/admin";
import { formatDateTime } from "utils/admin";

export type SchoolActionQueueItem = {
  description: string;
  id: string;
  label: string;
  meta?: string;
  onClick: () => void;
  tone?: "default" | "warning" | "live";
};

type SchoolActionQueueProps = {
  items: SchoolActionQueueItem[];
  title?: string;
};

const toneClasses: Record<NonNullable<SchoolActionQueueItem["tone"]>, string> = {
  default:
    "border-[var(--brand-border)] bg-[rgba(255,255,255,0.04)] text-[var(--brand-ink)] hover:bg-[rgba(255,255,255,0.06)]",
  live:
    "border-[rgba(52,211,153,0.35)] bg-[rgba(16,185,129,0.12)] text-[var(--brand-ink)] hover:bg-[rgba(16,185,129,0.18)]",
  warning:
    "border-[rgba(240,191,86,0.4)] bg-[rgba(240,191,86,0.1)] text-[var(--brand-ink)] hover:bg-[rgba(240,191,86,0.16)]",
};

export const buildSchoolActionQueueItems = ({
  onOpenMatchHub,
  onOpenSchedule,
  onOpenSchoolDocuments,
  onOpenScoresheet,
  schoolDashboard,
}: {
  onOpenMatchHub: (matchId: string) => void;
  onOpenSchedule: (search?: string) => void;
  onOpenSchoolDocuments: () => void;
  onOpenScoresheet: (matchId: string) => void;
  schoolDashboard?: any;
}): SchoolActionQueueItem[] => {
  const items: SchoolActionQueueItem[] = [];

  (schoolDashboard?.roster_tasks || []).forEach((task: any) => {
    const matchId = task?.school_match_id || task?.match_id || task?.school_match?.id;
    const openTask = matchId
      ? () => onOpenMatchHub(String(matchId))
      : () => onOpenSchedule("?view=calendar");

    items.push({
      description: `${task.school_team_name || "Team"} needs a roster before matchday.`,
      id: `roster-task-${task.id || `${task.school_team_id}-${task.scheduled_at}`}`,
      label: "Roster due",
      meta: formatDateTime(task.scheduled_at),
      onClick: openTask,
      tone: "warning",
    });
  });

  (schoolDashboard?.upcoming_matches || []).forEach((match: any) => {
    if (!match?.id) {
      return;
    }

    if (!match.scoresheet_summary?.id) {
      items.push({
        description: `${match.home_school_team?.name || "Home"} vs ${
          match.away_school_team?.name || "Away"
        } needs a scoresheet started.`,
        id: `scoresheet-setup-${match.id}`,
        label: "Start scoresheet",
        meta: formatDateTime(match.scheduled_at),
        onClick: () => onOpenScoresheet(String(match.id)),
        tone: "warning",
      });
    } else if (match.scoresheet_summary?.status === "live") {
      items.push({
        description: "This fixture is live in the scorer.",
        id: `scoresheet-live-${match.id}`,
        label: "Live scoring",
        meta: formatDateTime(match.scheduled_at),
        onClick: () => onOpenScoresheet(String(match.id)),
        tone: "live",
      });
    }
  });

  const documentSummary = schoolDashboard?.document_summary;
  if (
    documentSummary &&
    Number(documentSummary.total_count || 0) > Number(documentSummary.verified_count || 0)
  ) {
    items.push({
      description: `${documentSummary.verified_count ?? 0} of ${
        documentSummary.total_count ?? 0
      } school documents verified.`,
      id: "documents-verification",
      label: "Documents need review",
      onClick: onOpenSchoolDocuments,
      tone: "warning",
    });
  }

  if (items.length === 0 && (schoolDashboard?.upcoming_matches || []).length > 0) {
    items.push({
      description: "Review upcoming fixtures and roster deadlines in the schedule workspace.",
      id: "open-schedule",
      label: "Open schedule",
      meta: `${schoolDashboard.upcoming_matches.length} upcoming fixtures`,
      onClick: () => onOpenSchedule(),
    });
  }

  return items;
};

export const SchoolActionQueue: React.FC<SchoolActionQueueProps> = ({
  items,
  title = "Needs attention",
}) => {
  const visibleItems = useMemo(() => items.slice(0, 8), [items]);

  if (!visibleItems.length) {
    return null;
  }

  return (
    <section className="grid gap-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-[var(--brand-ink)]">{title}</h2>
        <span className="text-xs text-[var(--brand-muted)]">{visibleItems.length} open</span>
      </div>

      <div className="grid max-h-[22rem] gap-3 overflow-y-auto pr-1">
        {visibleItems.map((item) => (
          <button
            className={`rounded-2xl border p-4 text-left transition hover:border-[var(--brand-highlight)] ${
              toneClasses[item.tone || "default"]
            }`}
            key={item.id}
            onClick={item.onClick}
            type="button"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-[var(--brand-ink)]">{item.label}</p>
                <p className="mt-1 text-sm text-[var(--brand-muted)]">{item.description}</p>
              </div>
              {item.tone === "live" ? <StatusPill value="live" /> : null}
            </div>
            {item.meta ? <p className="mt-2 text-xs text-[var(--brand-muted)]">{item.meta}</p> : null}
          </button>
        ))}
      </div>
    </section>
  );
};
